import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { motion, useScroll, useTransform } from 'framer-motion'
import {
  FadeInUp,
  FadeInLeft,
  FadeInRight,
  StaggerContainer,
  StaggerItem,
  AnimatedCounter,
  HoverCard,
  FloatingElement,
  AnimatedGradient
} from '../Components/Animations'

// Design Tokens
const colors = {
  primary: '#0d9488',
  primaryDark: '#0f766e',
  primaryLight: '#14b8a6',
  secondary: '#0891b2',
  dark: '#0f172a',
  darkBlue: '#1e3a5f',
  text: '#1e293b',
  textLight: '#64748b',
  background: '#ffffff',
  backgroundAlt: '#f8fafc',
  accent: '#5eead4'
}

const Container = styled.div`
  width: 100%;
  min-height: 100vh;
  background-color: ${colors.background};
  font-family: -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif;
  font-size: 18px;
  line-height: 1.8;
  color: ${colors.text};
  overflow-x: hidden;
`

const MaxWidthContainer = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 24px;
  @media (min-width: 768px) {
    padding: 0 48px;
  }
`

const HeroSection = styled(motion.section)`
  min-height: 100vh;
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, ${colors.dark} 0%, ${colors.darkBlue} 50%, ${colors.primary} 100%);
  background-size: 400% 400%;
  animation: gradientShift 15s ease infinite;

  @keyframes gradientShift {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background:
      radial-gradient(circle at 20% 30%, rgba(14, 165, 233, 0.15) 0%, transparent 40%),
      radial-gradient(circle at 80% 70%, rgba(20, 184, 166, 0.15) 0%, transparent 40%);
    pointer-events: none;
  }
`

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  padding: 120px 0 80px;
`

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 48px;
  align-items: center;

  @media (min-width: 1024px) {
    grid-template-columns: 1fr 1fr;
    gap: 64px;
  }
`

const HeroTextContent = styled.div`
  color: white;
`

const HeroBadge = styled(motion.span)`
  display: inline-block;
  background: rgba(94, 234, 212, 0.15);
  color: ${colors.accent};
  padding: 8px 16px;
  border-radius: 100px;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.5px;
  margin-bottom: 24px;
  border: 1px solid rgba(94, 234, 212, 0.3);
`

const HeroTitle = styled(motion.h1)`
  font-size: clamp(36px, 5vw, 64px);
  font-weight: 700;
  line-height: 1.1;
  margin-bottom: 24px;
  letter-spacing: -0.02em;
`

const HeroSubtitle = styled(motion.p)`
  font-size: clamp(16px, 2vw, 20px);
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 40px;
  max-width: 540px;
`

const AnimatedService = styled(motion.span)`
  color: ${colors.accent};
  display: inline-block;
`

const ButtonGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`

const PrimaryButton = styled(motion.button)`
  background: linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%);
  color: white;
  font-size: 16px;
  font-weight: 600;
  padding: 16px 32px;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 20px rgba(13, 148, 136, 0.4);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 8px 30px rgba(13, 148, 136, 0.5);
  }
`

const SecondaryButton = styled(motion.button)`
  background: transparent;
  color: white;
  font-size: 16px;
  font-weight: 600;
  padding: 16px 32px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.5);
  }
`

const HeroImageWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;

  @media (max-width: 1023px) {
    order: -1;
  }
`

const HeroImage = styled(motion.img)`
  width: 100%;
  max-width: 500px;
  height: auto;
  border-radius: 24px;
  box-shadow: 0 40px 80px rgba(0, 0, 0, 0.3);
`

const Section = styled.section`
  padding: 100px 0;
  position: relative;

  @media (max-width: 768px) {
    padding: 64px 0;
  }
`

const SectionHeader = styled.div`
  text-align: center;
  max-width: 680px;
  margin: 0 auto 64px;
`

const SectionLabel = styled.span`
  display: inline-block;
  color: ${colors.primary};
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin-bottom: 16px;
`

const SectionTitle = styled.h2`
  font-size: clamp(28px, 4vw, 42px);
  font-weight: 700;
  color: ${colors.text};
  margin-bottom: 20px;
  letter-spacing: -0.02em;
  line-height: 1.2;
`

const SectionDescription = styled.p`
  font-size: 18px;
  color: ${colors.textLight};
  line-height: 1.7;
`

const OverviewSection = styled(Section)`
  background: linear-gradient(180deg, ${colors.backgroundAlt} 0%, ${colors.background} 100%);
`

const OverviewGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 48px;
  align-items: center;

  @media (min-width: 1024px) {
    grid-template-columns: 1fr 1fr;
    gap: 80px;
  }
`

const OverviewContent = styled.div``

const OverviewText = styled.p`
  font-size: 18px;
  color: ${colors.textLight};
  line-height: 1.8;
  margin-bottom: 24px;
`

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 32px 0;
`

const FeatureItem = styled(motion.li)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  font-size: 16px;
  color: ${colors.text};

  svg {
    color: ${colors.primary};
    flex-shrink: 0;
  }
`

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  background: white;
  padding: 32px;
  border-radius: 20px;
  box-shadow: 0 4px 40px rgba(0, 0, 0, 0.06);
`

const StatItem = styled.div`
  text-align: center;
`

const StatNumber = styled.div`
  font-size: clamp(32px, 4vw, 48px);
  font-weight: 700;
  color: ${colors.primary};
  line-height: 1;
  margin-bottom: 8px;
`

const StatLabel = styled.div`
  font-size: 14px;
  color: ${colors.textLight};
`

const ServicesSection = styled(Section)`
  background: ${colors.background};
`

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
`

const ServiceCard = styled(motion.div)`
  background: white;
  padding: 32px;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    border-color: ${colors.primary};
    box-shadow: 0 20px 40px rgba(13, 148, 136, 0.1);
  }
`

const ServiceIcon = styled.div`
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, ${colors.primary}15 0%, ${colors.secondary}15 100%);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  margin-bottom: 20px;
`

const ServiceTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: ${colors.text};
  margin-bottom: 12px;
`

const ServiceDescription = styled.p`
  font-size: 15px;
  color: ${colors.textLight};
  line-height: 1.6;
  margin-bottom: 16px;
`

const ServiceLink = styled.span`
  color: ${colors.primary};
  font-weight: 600;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &:hover {
    gap: 10px;
  }
`

const IndustriesSection = styled(Section)`
  background: ${colors.backgroundAlt};
`

const IndustriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 20px;
`

const IndustryCard = styled(motion.div)`
  background: white;
  padding: 32px 24px;
  border-radius: 16px;
  text-align: center;
  border: 1px solid #e2e8f0;
  transition: all 0.3s ease;

  &:hover {
    border-color: ${colors.primary};
    transform: translateY(-4px);
  }
`

const IndustryIcon = styled.div`
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  margin: 0 auto 16px;
`

const IndustryTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${colors.text};
`

const CTASection = styled(Section)`
  background: linear-gradient(135deg, ${colors.dark} 0%, ${colors.darkBlue} 100%);
  color: white;
  text-align: center;
`

const CTATitle = styled.h2`
  font-size: clamp(28px, 4vw, 42px);
  font-weight: 700;
  margin-bottom: 16px;
  letter-spacing: -0.02em;
`

const CTASubtitle = styled.p`
  font-size: 18px;
  opacity: 0.9;
  margin-bottom: 40px;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
`

const CTAButton = styled(motion.button)`
  background: white;
  color: ${colors.primary};
  font-size: 16px;
  font-weight: 600;
  padding: 18px 40px;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(255, 255, 255, 0.2);
`

const CaseStudiesSection = styled(Section)`
  background: ${colors.background};
`

const CaseStudiesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
  gap: 32px;
`

const CaseStudyCard = styled(motion.div)`
  background: white;
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
  }
`

const CaseStudyImage = styled.div`
  height: 180px;
  background: linear-gradient(135deg, ${colors.dark} 0%, ${colors.primary} 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 18px;
  font-weight: 600;
`

const CaseStudyContent = styled.div`
  padding: 28px;
`

const CaseStudyTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: ${colors.text};
  margin-bottom: 12px;
`

const CaseStudyText = styled.p`
  font-size: 15px;
  color: ${colors.textLight};
  line-height: 1.6;
  margin-bottom: 16px;
`

const CaseStudyImpact = styled.div`
  display: inline-block;
  background: ${colors.primary}15;
  color: ${colors.primary};
  padding: 8px 16px;
  border-radius: 100px;
  font-size: 14px;
  font-weight: 600;
`

const ValuesSection = styled(Section)`
  background: ${colors.backgroundAlt};
`

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
`

const ValueCard = styled(motion.div)`
  background: white;
  padding: 28px;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
`

const ValueTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${colors.primary};
  margin-bottom: 12px;
`

const ValueText = styled.p`
  font-size: 15px;
  color: ${colors.textLight};
  line-height: 1.6;
`

const TestimonialsSection = styled(Section)`
  background: ${colors.background};
`

const TestimonialsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 24px;
`

const TestimonialCard = styled(motion.div)`
  background: white;
  padding: 28px;
  border-radius: 16px;
  border-left: 4px solid ${colors.primary};
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
`

const TestimonialQuote = styled.p`
  font-size: 16px;
  color: ${colors.text};
  line-height: 1.7;
  margin-bottom: 20px;
  font-style: italic;
`

const TestimonialAuthor = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.primary};
`

const ContactSection = styled(Section)`
  background: linear-gradient(180deg, ${colors.backgroundAlt} 0%, ${colors.background} 100%);
`

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 48px;
  align-items: center;

  @media (min-width: 1024px) {
    grid-template-columns: 1fr 1fr;
    gap: 80px;
  }
`

const ContactContent = styled.div``

const ContactImage = styled(motion.img)`
  width: 100%;
  max-width: 480px;
  height: auto;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
`

const RSNGHome = () => {
  const navigate = useNavigate()
  const [currentService, setCurrentService] = useState(0)
  const { scrollYProgress } = useScroll()
  const heroY = useTransform(scrollYProgress, [0, 0.5], [0, -100])

  const services = [
    'Product Development',
    'Cloud Solutions',
    'CI/CD Automation',
    'UI/UX Design',
    'Staff Augmentation',
    'Data Science',
    'Quality Assurance',
    'AI & Machine Learning',
    'Salesforce Solutions',
  ]

  useEffect(() => {
    window.scrollTo(0, 0)
    const interval = setInterval(() => {
      setCurrentService((prev) => (prev + 1) % services.length)
    }, 2500)
    return () => clearInterval(interval)
  }, [services.length])

  const servicesList = [
    { icon: '💻', title: 'Product Development', description: 'From concept to deployment, we build scalable software solutions that exceed expectations.' },
    { icon: '☁️', title: 'Cloud Solutions', description: 'Kubernetes, auto-scaling infrastructure, and cost optimization for maximum performance.' },
    { icon: '🔄', title: 'CI/CD Automation', description: 'Automated pipelines for faster delivery and improved software quality.' },
    { icon: '🎨', title: 'UI/UX Design', description: 'Intuitive interfaces that enhance engagement and deliver exceptional experiences.' },
    { icon: '👥', title: 'Staff Augmentation', description: 'Flexible offshore talent to scale your team with specialized expertise.' },
    { icon: '📊', title: 'Data Science', description: 'Advanced analytics to drive smarter decisions and strategic growth.' },
    { icon: '✅', title: 'Quality Assurance', description: 'Comprehensive testing strategies for robust, error-free releases.' },
    { icon: '🤖', title: 'AI & ML Solutions', description: 'Intelligent automation and predictive insights for innovation.' },
    { icon: '⚡', title: 'Salesforce Solutions', description: 'CRM optimization and customization to maximize your ROI.' }
  ]

  const industries = [
    { icon: '🏥', title: 'Healthcare' },
    { icon: '🛒', title: 'E-commerce' },
    { icon: '🏦', title: 'Finance' },
    { icon: '🛡️', title: 'Insurance' },
    { icon: '📱', title: 'Telecom' },
    { icon: '🚀', title: 'Startups' }
  ]

  const caseStudies = [
    { title: 'E-commerce Platform', description: 'Reduced deployment time by 85% through CI/CD automation.', impact: '85% faster deployments' },
    { title: 'FinTech Startup', description: 'Achieved 40% cloud cost savings with Kubernetes migration.', impact: '40% cost reduction' },
    { title: 'SaaS Provider', description: 'Delivered 99.99% uptime with comprehensive SRE implementation.', impact: '99.99% uptime' }
  ]

  const testimonials = [
    { quote: 'RSNGINFO transformed our deployment process, saving hours and reducing errors significantly.', author: 'CTO, Leading E-commerce Platform' },
    { quote: 'Their cloud optimization cut our infrastructure costs by nearly half while boosting performance.', author: 'Head of IT, FinTech Startup' },
    { quote: 'Thanks to their SRE implementation, our platform has maintained flawless uptime.', author: 'Product Manager, SaaS Company' }
  ]

  const coreValues = [
    { title: 'Accountability', description: 'We stand behind our work and build lasting trust through transparency.' },
    { title: 'Integrity', description: 'Every decision is guided by strong moral principles and ethical practices.' },
    { title: 'Responsibility', description: 'We deliver on time with uncompromising quality.' },
    { title: 'Creativity', description: 'We solve problems with fresh perspectives and innovative solutions.' },
    { title: 'Honesty', description: 'Open communication and genuine partnerships built on trust.' }
  ]

  return (
    <Container>
      <HeroSection>
        <MaxWidthContainer>
          <HeroContent>
            <HeroGrid>
              <HeroTextContent>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <HeroBadge>Digital Transformation Partner</HeroBadge>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                >
                  <HeroTitle>
                    Transform Ideas Into Reality with{' '}
                    <AnimatedService
                      key={currentService}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.4 }}
                    >
                      {services[currentService]}
                    </AnimatedService>
                  </HeroTitle>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <HeroSubtitle>
                    Your partner in innovation and tailored digital solutions. We deliver future-ready technology to empower businesses in today's digital-first world.
                  </HeroSubtitle>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                >
                  <ButtonGroup>
                    <PrimaryButton
                      onClick={() => navigate('/contact')}
                      whileHover={{ scale: 1.02, y: -2 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Get Started
                      <span>→</span>
                    </PrimaryButton>
                    <SecondaryButton
                      onClick={() => navigate('/services')}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Our Services
                    </SecondaryButton>
                  </ButtonGroup>
                </motion.div>
              </HeroTextContent>
              <HeroImageWrapper>
                <FloatingElement duration={4} y={20}>
                  <HeroImage
                    src="https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&q=80"
                    alt="Digital Solutions"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                  />
                </FloatingElement>
              </HeroImageWrapper>
            </HeroGrid>
          </HeroContent>
        </MaxWidthContainer>
      </HeroSection>

      <OverviewSection>
        <MaxWidthContainer>
          <OverviewGrid>
            <OverviewContent>
              <FadeInLeft>
                <SectionLabel>About Us</SectionLabel>
                <SectionTitle style={{ textAlign: 'left' }}>Who We Are: Digital Transformation Leaders</SectionTitle>
                <OverviewText>
                  Founded in 2019, RSNGINFO SOLUTIONS has evolved into a powerhouse of digital innovation with 150+ experts dedicated to transforming ideas into reality.
                </OverviewText>
                <OverviewText>
                  We deliver innovative solutions, tailored strategies, and future-ready technology to empower businesses in today's digital-first world.
                </OverviewText>
                <PrimaryButton
                  onClick={() => navigate('/about')}
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  style={{ marginTop: '16px' }}
                >
                  Learn More
                  <span>→</span>
                </PrimaryButton>
              </FadeInLeft>
            </OverviewContent>
            <FadeInRight>
              <StatsGrid>
                <StatItem>
                  <StatNumber><AnimatedCounter end={150} suffix="+" /></StatNumber>
                  <StatLabel>Team Members</StatLabel>
                </StatItem>
                <StatItem>
                  <StatNumber><AnimatedCounter end={49} prefix="4." suffix="/5" /></StatNumber>
                  <StatLabel>Employee Rating</StatLabel>
                </StatItem>
                <StatItem>
                  <StatNumber>2019</StatNumber>
                  <StatLabel>Founded</StatLabel>
                </StatItem>
              </StatsGrid>
            </FadeInRight>
          </OverviewGrid>
        </MaxWidthContainer>
      </OverviewSection>

      <IndustriesSection>
        <MaxWidthContainer>
          <FadeInUp>
            <SectionHeader>
              <SectionLabel>Industries</SectionLabel>
              <SectionTitle>Expertise Across Sectors</SectionTitle>
              <SectionDescription>
                We bring specialized knowledge to diverse industries, delivering tailored solutions for unique challenges.
              </SectionDescription>
            </SectionHeader>
          </FadeInUp>
          <StaggerContainer staggerDelay={0.1}>
            <IndustriesGrid>
              {industries.map((industry, index) => (
                <StaggerItem key={index}>
                  <HoverCard scale={1.03}>
                    <IndustryCard>
                      <IndustryIcon>{industry.icon}</IndustryIcon>
                      <IndustryTitle>{industry.title}</IndustryTitle>
                    </IndustryCard>
                  </HoverCard>
                </StaggerItem>
              ))}
            </IndustriesGrid>
          </StaggerContainer>
        </MaxWidthContainer>
      </IndustriesSection>

      <CaseStudiesSection>
        <MaxWidthContainer>
          <FadeInUp>
            <SectionHeader>
              <SectionLabel>Case Studies</SectionLabel>
              <SectionTitle>Proven Results for Leading Clients</SectionTitle>
              <SectionDescription>
                Our track record speaks volumes. See how we've helped organizations achieve remarkable transformations.
              </SectionDescription>
            </SectionHeader>
          </FadeInUp>
          <StaggerContainer staggerDelay={0.15}>
            <CaseStudiesGrid>
              {caseStudies.map((study, index) => (
                <StaggerItem key={index}>
                  <HoverCard>
                    <CaseStudyCard>
                      <CaseStudyImage>{study.title}</CaseStudyImage>
                      <CaseStudyContent>
                        <CaseStudyTitle>{study.title}</CaseStudyTitle>
                        <CaseStudyText>{study.description}</CaseStudyText>
                        <CaseStudyImpact>{study.impact}</CaseStudyImpact>
                      </CaseStudyContent>
                    </CaseStudyCard>
                  </HoverCard>
                </StaggerItem>
              ))}
            </CaseStudiesGrid>
          </StaggerContainer>
        </MaxWidthContainer>
      </CaseStudiesSection>

      <TestimonialsSection>
        <MaxWidthContainer>
          <FadeInUp>
            <SectionHeader>
              <SectionLabel>Testimonials</SectionLabel>
              <SectionTitle>What Our Clients Say</SectionTitle>
              <SectionDescription>
                Don't just take our word for it. Hear from our satisfied clients.
              </SectionDescription>
            </SectionHeader>
          </FadeInUp>
          <StaggerContainer staggerDelay={0.15}>
            <TestimonialsGrid>
              {testimonials.map((testimonial, index) => (
                <StaggerItem key={index}>
                  <HoverCard>
                    <TestimonialCard>
                      <TestimonialQuote>"{testimonial.quote}"</TestimonialQuote>
                      <TestimonialAuthor>{testimonial.author}</TestimonialAuthor>
                    </TestimonialCard>
                  </HoverCard>
                </StaggerItem>
              ))}
            </TestimonialsGrid>
          </StaggerContainer>
        </MaxWidthContainer>
      </TestimonialsSection>

      <ContactSection>
        <MaxWidthContainer>
          <ContactGrid>
            <ContactContent>
              <FadeInLeft>
                <SectionLabel>Get In Touch</SectionLabel>
                <SectionTitle style={{ textAlign: 'left' }}>Let's Build the Future Together</SectionTitle>
                <OverviewText>
                  Ready to transform your business? Contact us today and discover how our innovative solutions can accelerate your digital transformation journey.
                </OverviewText>
                <OverviewText>
                  Join the growing list of forward-thinking businesses empowered by our reliable software solutions.
                </OverviewText>
                <PrimaryButton
                  onClick={() => navigate('/contact')}
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  style={{ marginTop: '16px' }}
                >
                  Contact Us Today
                  <span>→</span>
                </PrimaryButton>
              </FadeInLeft>
            </ContactContent>
            <FadeInRight>
              <ContactImage
                src="https://images.unsplash.com/photo-1423666639041-f56000c27a9a?w=800&q=80"
                alt="Contact Us"
              />
            </FadeInRight>
          </ContactGrid>
        </MaxWidthContainer>
      </ContactSection>
    </Container>
  )
}

export default RSNGHome
